import { Accordion, InfoList, Loading, SectionHeaderCustom } from "@landtechnologies/components";
import { Dispatch, FC, SetStateAction, useEffect, useState } from "react";
import clsx from "clsx";

import { useTranslation } from "react-migration/lib";

import { PriceModes, RecordType } from "../../../typings";
import { AddressItem } from "../AddressItem";
import { AdditionalLandTechTransactions } from "../AdditionalTransactions/AdditionalLandTechTransactions";
import { isZoopla } from "react-migration/domains/comparables/util/isZoopla";
import { AdditionalZooplaTransactions } from "../AdditionalTransactions/AdditionalZooplaTransactions";
import { DisplayPillBox } from "react-migration/domains/search/components/FilterList/filters/DisplayPillBox";

export enum TransactionsAccordionStates {
  INCLUDED = "includedResults",
  EXCLUDED = "excludedResults",
  CLOSED = "",
}

const Arrow = ({ header, state }: { header: string; state: TransactionsAccordionStates }) => {
  return (
    <div
      className="atlas-rounded atlas-color-dark atlas-pointer-events-none atlas-flex atlas-items-center"
      aria-hidden="true"
    >
      <i
        className={clsx(
          "icon-lt-arrow-up-s-line atlas-text-xl atlas-duration-300",
          state === header ? "" : "atlas-rotate-180"
        )}
      />
    </div>
  );
};
export interface TransactionsProps {
  accordionState: TransactionsAccordionStates;
  setAccordionState: Dispatch<SetStateAction<TransactionsAccordionStates>>;
  hideExcludedResults?: boolean;
  includedResults: RecordType[];
  excludedResults: RecordType[];
  loading?: boolean;
  setSelectedRecord: (record: RecordType) => void;
  priceMode: PriceModes;
  distribution?: number[];
}

export const Transactions: FC<TransactionsProps> = ({
  accordionState,
  setAccordionState,
  hideExcludedResults,
  includedResults,
  excludedResults,
  loading,
  setSelectedRecord,
  priceMode,
  distribution,
}) => {
  const { t } = useTranslation();
  const [showLoader, setShowLoader] = useState(loading);
  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>;

    if (loading && !showLoader) {
      timeout = setTimeout(() => setShowLoader(true), 600);
    }

    return () => {
      if (loading) {
        setShowLoader(false);
      }
      clearTimeout(timeout);
    };
  }, [loading]);

  const includedUPRNs = includedResults.map((record) => record.uprn).filter(Boolean) as [];
  const excludedUPRNs = excludedResults.map((record) => record.uprn).filter(Boolean) as [];

  return (
    <Accordion
      type="single"
      defaultValue={accordionState}
      collapsible={true}
      onValueChange={(value: TransactionsAccordionStates) => setAccordionState(value)}
      data-testid="transactions"
    >
      <Accordion.Item
        value={TransactionsAccordionStates.INCLUDED}
        className="atlas-border-neutral-200 atlas-border-t"
        disabled={includedResults.length === 0}
      >
        <Accordion.Trigger>
          <SectionHeaderCustom>
            <div className="atlas-flex atlas-flex-row atlas-items-center atlas-justify-between atlas-cursor-pointer">
              {loading && showLoader ? (
                <div>
                  <div className="atlas-inline-block atlas-text-3xs atlas-px-1">
                    <Loading relativeSizing />
                  </div>
                </div>
              ) : (
                <div className="atlas-flex atlas-items-center">
                  <DisplayPillBox className="!atlas-bg-background-darker !atlas-rounded-lg">
                    {includedResults.length}
                  </DisplayPillBox>
                  <div className="atlas-flex-grow atlas-pl-2">
                    {t("comparables.map.sidebar.value.match")}
                  </div>
                </div>
              )}
              <Arrow header={TransactionsAccordionStates.INCLUDED} state={accordionState} />
            </div>
          </SectionHeaderCustom>
        </Accordion.Trigger>
        <Accordion.Content>
          <InfoList className="atlas-divide-y atlas-divide-neutral-200 atlas-divide-solid">
            {includedResults.map((transaction: RecordType) => {
              return [
                <AddressItem
                  key={`${transaction.transaction_id}-address-item`}
                  address={transaction.address}
                  currency={transaction.currency ?? "GBP"}
                  dateOfRecord={transaction.date_of_transfer}
                  distribution={distribution}
                  floorArea={transaction.total_floor_area ?? undefined}
                  price={transaction.price}
                  priceMode={priceMode}
                  setSelectedRecord={() => setSelectedRecord(transaction)}
                  uprns={includedUPRNs}
                  uprn={transaction.uprn}
                />,
                isZoopla(priceMode) ? (
                  <AdditionalZooplaTransactions
                    key={`${transaction.transaction_id}-additional-transactions`}
                    transaction={transaction}
                  />
                ) : (
                  <AdditionalLandTechTransactions
                    key={`${transaction.transaction_id}-additional-transactions`}
                    transaction={transaction}
                  />
                ),
              ];
            })}
          </InfoList>
        </Accordion.Content>
      </Accordion.Item>
      {hideExcludedResults ? null : (
        <Accordion.Item
          value={TransactionsAccordionStates.EXCLUDED}
          disabled={excludedResults.length === 0}
        >
          <Accordion.Trigger>
            <SectionHeaderCustom>
              <div className="atlas-flex atlas-flex-row atlas-items-center atlas-justify-between atlas-cursor-pointer">
                {loading && showLoader ? (
                  <div>
                    {t("comparables.map.sidebar.value.nomatch_loading")} (
                    <div className="atlas-inline-block atlas-text-3xs atlas-px-1">
                      <Loading relativeSizing />
                    </div>
                    )
                  </div>
                ) : (
                  t("comparables.map.sidebar.value.nomatch", {
                    number: excludedResults.length,
                  })
                )}
                <Arrow header={TransactionsAccordionStates.EXCLUDED} state={accordionState} />
              </div>
            </SectionHeaderCustom>
          </Accordion.Trigger>
          <Accordion.Content>
            <InfoList className="atlas-divide-y atlas-divide-neutral-200 atlas-divide-solid">
              {excludedResults.map((transaction: RecordType) => {
                return [
                  <AddressItem
                    key={`${transaction.transaction_id}-address-item`}
                    address={transaction.address}
                    currency={transaction.currency ?? "GBP"}
                    dateOfRecord={transaction.date_of_transfer}
                    distribution={distribution}
                    floorArea={transaction.total_floor_area ?? undefined}
                    price={transaction.price}
                    priceMode={priceMode}
                    setSelectedRecord={() => setSelectedRecord(transaction)}
                    uprns={excludedUPRNs}
                    uprn={transaction.uprn}
                  />,
                  isZoopla(priceMode) ? (
                    <AdditionalZooplaTransactions
                      key={`${transaction.transaction_id}-additional-transactions`}
                      transaction={transaction}
                    />
                  ) : (
                    <AdditionalLandTechTransactions
                      key={`${transaction.transaction_id}-additional-transactions`}
                      transaction={transaction}
                    />
                  ),
                ];
              })}
            </InfoList>
          </Accordion.Content>
        </Accordion.Item>
      )}
    </Accordion>
  );
};
